/**
 * PALISADE CUSTOM — "Scroll to section" button action.
 *
 * Any element carrying `data-palisade-scroll-to="<css selector>"` scrolls the
 * page to that selector on click instead of navigating — and, which is the whole
 * point of the feature, WITHOUT appending a `#fragment` to the address bar.
 *
 * Delegated from the document rather than bound per element, so it covers
 * buttons rendered by any section or block, including ones the theme editor
 * injects after load.
 *
 * The trigger keeps its real `href` wherever there is one. If the selector is
 * not present on the current page, this handler stays out of the way and lets
 * the browser navigate normally — so a cross-page CTA (a header "Shop now"
 * pointing at /#buy-box, clicked from a blog article) still works, and the href
 * remains a genuine no-JS fallback.
 */

const TRIGGER_SELECTOR = '[data-palisade-scroll-to]';

/**
 * base.css declares `scroll-behavior: smooth` unconditionally on `html` and
 * again on `.page-wrapper`, with no `prefers-reduced-motion` opt-out — so the
 * CSS never honours reduced motion. Deciding here keeps this feature correct
 * regardless of that.
 *
 * PALISADE CUSTOM 2026-09-01: 'instant', not 'auto'. Per CSSOM-View,
 * `behavior: 'auto'` means "use the scroll-behavior property of the scrolling
 * box" — which resolves straight back to `smooth`, so the previous 'auto'
 * silently animated for exactly the visitors who asked it not to. 'instant' is
 * the value that actually defeats the CSS. It is also load-bearing for the
 * fragment-landing correction below, which has to beat an in-flight smooth
 * animation rather than queue behind it.
 */
function scrollBehavior() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
}

/**
 * The value is merchant-entered, so an invalid selector must not throw on every
 * click for the rest of the session.
 */
function resolveTarget(rawSelector) {
  const selector = (rawSelector || '').trim();
  if (!selector) return null;

  try {
    return document.querySelector(selector);
  } catch {
    console.warn(`[palisade-scroll-to] Ignoring invalid CSS selector: ${selector}`);
    return null;
  }
}

/**
 * Land the section below the sticky header. `--header-height` is maintained live
 * on <body> by palisade-header.js. Skipped when the target already declares its
 * own scroll-margin-top, so the 90px on snippets/palisade-frame.liquid anchors
 * keeps winning.
 */
function applyHeaderOffset(target) {
  const declared = parseFloat(window.getComputedStyle(target).scrollMarginTop);
  if (declared) return declared;

  target.style.scrollMarginTop = 'calc(var(--header-height, 80px) + 1rem)';
  // Returned because the squeeze-layout path below scrolls the wrapper by hand
  // and has to subtract the offset itself; scrollIntoView applies it natively.
  return parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
}

/**
 * Send focus where sighted users were just sent. Without this the focus ring
 * stays on the CTA and the next Tab resumes from the header rather than the
 * section that was scrolled to (WCAG 2.4.3 Focus Order).
 *
 * Skipped for aria-hidden targets — palisade-frame.liquid anchors are empty
 * aria-hidden spans, and focusing one would drop focus onto a node that is
 * hidden from assistive tech.
 */
function moveFocus(target) {
  if (target.getAttribute('aria-hidden') === 'true') return;

  if (!target.hasAttribute('tabindex') && !target.matches('a[href], button, input, select, textarea')) {
    target.setAttribute('tabindex', '-1');
  }

  target.focus({ preventScroll: true });
}

/* ---------------------------------------------------------------------------
 * PALISADE CUSTOM 2026-09-01 — #fragment landings on the desktop layout.
 *
 * Reported as: "submitting my email in the footer loads a weird white space,
 * kicks me to the top and scrolls me down to white space below the footer."
 *
 * At >=990px base.css locks html/body to `height: 100dvh; overflow: hidden`
 * and makes `.page-wrapper` the real scroll port. A #fragment navigation is
 * `scrollIntoView({block:'start'})`, which scrolls EVERY ancestor scroll port
 * in turn. When the target sits inside the last viewport-height of the page,
 * `.page-wrapper` CLAMPS at its maximum scrollTop and cannot bring the target
 * up to its own top edge — so the unabsorbed remainder is applied to the
 * VIEWPORT instead. html/body being `overflow: hidden` means there is no
 * scrollbar to undo that, so the page just sits offset and everything below
 * the 100dvh wrapper paints as bare body background.
 *
 * Measured on the live homepage at 1440x900: window.scrollY 537,
 * .page-wrapper.scrollTop 8791 (its max) — and zeroing the window alone put
 * the footer and its success message back on screen with no white band.
 *
 * Shopify bakes the fragment into the form's own action (`{% form 'customer' %}`
 * renders action="/contact#contact_form"), so it survives the redirect back to
 * ?customer_posted=true and EVERY newsletter/contact submit lands one. The
 * footer form is on every page, so this is not a homepage-only defect. It is
 * also not form-only: any deep link to an anchor in the last screenful does it.
 *
 * Confirmed NOT to fire for mid-page targets: loading /#buy-box leaves
 * window.scrollY at 0, because the wrapper can satisfy that target by itself.
 * ------------------------------------------------------------------------ */

/**
 * The squeeze layout's real scroll port, or null when the window is the
 * scroller and none of this should run.
 *
 * Reads the CSS that CAUSES the bug rather than re-matching `(min-width: 990px)`,
 * so it stays true if the breakpoint moves and on layouts with no wrapper
 * (layout/password.liquid). Verified both ways: at 1440px the wrapper computes
 * `overflow-y: auto` with scrollHeight 9691 > clientHeight 900; at 390px it
 * computes `visible` with scrollHeight === clientHeight and the document
 * scrolls instead. It is also automatically null while a drawer holds
 * `html[scroll-lock]` (which flips the wrapper to `overflow: hidden`) — which
 * matters, because during a scroll-lock a non-zero window scroll is the
 * visitor's real position and must not be touched.
 */
function squeezeScroller() {
  const wrapper = document.querySelector('.page-wrapper');
  if (!wrapper) return null;

  const { overflowY } = window.getComputedStyle(wrapper);
  return overflowY === 'auto' || overflowY === 'scroll' ? wrapper : null;
}

/**
 * Hand the viewport's stolen offset back. Only ever called once
 * squeezeScroller() has confirmed the window is not the scroller, so any
 * non-zero offset here is by definition one the visitor cannot undo.
 *
 * 'instant' does two jobs: it skips `html { scroll-behavior: smooth }`, and
 * per CSSOM-View scrolling a box aborts that box's in-flight smooth scroll —
 * so a fragment animation still running cannot re-apply the offset later.
 */
function unstrandViewport() {
  if (window.scrollX === 0 && window.scrollY === 0) return;
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
}

/**
 * In the squeeze layout the viewport is not a scroller at all — `.page-wrapper`
 * is, and html/body are `overflow: hidden`. So `window.scrollY` there has
 * exactly one possible cause: something called scrollIntoView (or focused an
 * element) on a target the wrapper could not fully satisfy, and the browser put
 * the remainder on the viewport. There is no scrollbar to undo it, so the only
 * correct value is 0 — always, not just after a landing.
 *
 * That is why this is a standing listener rather than a timed guard. A #fragment
 * is only one of the causes; the Shopify THEME EDITOR is another and is not
 * something this file can hook. Selecting a section in the editor scrolls it
 * into view, and for a target in the last screenful the leftover lands on the
 * viewport exactly as it does for a form redirect. Reproduced on the live page
 * by calling scrollIntoView on each section in turn at 1440x900: the 14 main
 * content sections all leave scrollY at 0, the footer strands it by 271px, and
 * the trailing section by a full 900px — a whole viewport of white. That is the
 * "click a section in the admin and the page goes white" report.
 *
 * Cheap by construction: scroll events fired at an ELEMENT do not bubble, so a
 * `scroll` listener on `window` hears the viewport and nothing else — never
 * `.page-wrapper`, which is where all normal scrolling happens. It therefore
 * stays silent in the normal case and reads no layout. It cannot loop: the
 * scrollTo it issues re-fires `scroll` once at 0, which returns immediately.
 * And it is inert below 990px, where squeezeScroller() is null and the window's
 * scroll position is the visitor's real one — including during a mobile
 * `html[scroll-lock]`, which flips the wrapper to `overflow: hidden` and so
 * correctly reads as "not the squeeze layout".
 */
function guardViewport() {
  if (!squeezeScroller()) return;
  unstrandViewport();
}

/**
 * Scroll the scroll port itself so `target` lands at its top edge, minus the
 * resolved scroll-margin-top. Both rects shift equally with any current
 * viewport offset, so the delta between them is unaffected by it.
 */
function scrollWithinScroller(scroller, target, offset, behavior) {
  const distance = target.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
  const limit = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  const top = Math.min(Math.max(0, scroller.scrollTop + distance - offset), limit);

  scroller.scrollTo({ top, left: scroller.scrollLeft, behavior });
}

/**
 * The one way this file scrolls to anything. In the squeeze layout it drives
 * `.page-wrapper` directly instead of calling scrollIntoView, because
 * scrollIntoView is precisely the API that spills the unabsorbable remainder
 * onto the viewport. Elsewhere — mobile, layout/password.liquid, or a target
 * outside the wrapper such as one inside the cart drawer — scrollIntoView is
 * still correct and still walks nested scroll ports.
 */
function scrollTargetIntoView(target, behavior) {
  const offset = applyHeaderOffset(target);
  const scroller = squeezeScroller();

  if (scroller && scroller.contains(target)) {
    scrollWithinScroller(scroller, target, offset, behavior);
  } else {
    target.scrollIntoView({ behavior, block: 'start', inline: 'nearest' });
  }

  if (!scroller) return;

  unstrandViewport();
}

/** The element the current #fragment points at, resolved as the browser does. */
function fragmentTarget() {
  const raw = window.location.hash.slice(1);
  if (!raw) return null;

  let id = raw;
  try {
    id = decodeURIComponent(raw);
  } catch {
    // Malformed escape sequence — the browser did not resolve it either.
  }

  return document.getElementById(id) || document.getElementsByName(id)[0] || null;
}

/**
 * Once the visitor has taken over, stop re-applying the landing — mirroring the
 * browser's own rule for retrying a fragment anchor, so a late checkpoint on a
 * slow load cannot yank someone who has already scrolled away. Only real input
 * sets this; programmatic scrolls do not.
 */
let userScrollIntent = false;
let intentWatched = false;

function noteUserScrollIntent() {
  userScrollIntent = true;
}

function watchUserScrollIntent() {
  if (intentWatched) return;
  intentWatched = true;

  for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
    window.addEventListener(type, noteUserScrollIntent, { passive: true, once: true });
  }
}

/**
 * Re-do the landing properly: `.page-wrapper` keeps the scroll that puts the
 * target under the sticky header, and the viewport goes back to 0. Runs with no
 * fragment too, since anything else that calls scrollIntoView on load strands
 * the viewport by the same mechanism and leaves no hash behind.
 */
function correctFragmentLanding(behavior) {
  const scroller = squeezeScroller();
  if (!scroller) return;

  watchUserScrollIntent();

  const target = fragmentTarget();
  if (target && scroller.contains(target) && !userScrollIntent) {
    scrollWithinScroller(scroller, target, applyHeaderOffset(target), behavior);
  }

  unstrandViewport();
}

/**
 * Checkpoints, not a loop. This module is deferred, so the first call already
 * runs on a fully parsed document; load covers the browser's own fragment
 * retries as late media changes layout; pageshow covers bfcache restores.
 * Landings are 'instant' — the visitor has just arrived, the target IS the
 * initial state, and an instant scroll deterministically aborts any smooth
 * animation still in flight.
 */
/* Every listener and first call is registered HERE, at the foot of the module,
   after all bindings above are initialised. Registering mid-file is what caused
   the 2026-09-02 reveal-engine outage in assets/palisade-reveal.js: a handler
   reached state whose initialiser had not run yet, threw, and took the whole
   feature down. Nothing here reads `userScrollIntent` before it exists, but
   keeping the execution phase in one place removes the hazard class rather than
   relying on that staying true. */
window.addEventListener('scroll', guardViewport, { passive: true });

correctFragmentLanding('instant');

for (const type of ['load', 'pageshow']) {
  window.addEventListener(type, () => correctFragmentLanding('instant'));
}

/** A hashchange is fresh intent for a new target, so it animates like a click. */
window.addEventListener('hashchange', () => {
  userScrollIntent = false;
  correctFragmentLanding(scrollBehavior());
});

document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.(TRIGGER_SELECTOR);
  if (!trigger) return;

  // Leave modified clicks (open in new tab/window, middle click) to the browser.
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const target = resolveTarget(trigger.dataset.palisadeScrollTo);

  // Not on this page — fall through to the href so navigation still happens.
  if (!target) return;

  event.preventDefault();

  /**
   * Close an enclosing open <dialog> first. A trigger can live inside a drawer —
   * the cart drawer's empty-state "Continue shopping" scrolls to the buy box —
   * and scrolling the page behind a modal that is still covering it looks like
   * nothing happened. Only the ancestor dialog is touched, so a trigger out in
   * the page is unaffected.
   *
   * `close()` on the element rather than a synthetic Escape: Escape is
   * cancellable and a drawer may legitimately trap it, whereas close() also
   * fires the `close` event those components listen to for their own cleanup.
   */
  const dialog = trigger.closest('dialog[open]');
  if (dialog) dialog.close();

  /**
   * Not window.scrollTo: above 990px base.css gives `.page-wrapper`
   * `height: 100dvh; overflow-y: auto` and locks html/body to
   * `overflow: hidden`, so the window is not the scrolling element on desktop
   * and window.scrollTo would silently do nothing.
   *
   * PALISADE CUSTOM 2026-09-01: routed through scrollTargetIntoView(), which
   * drives whichever box actually scrolls, applies the sticky-header offset and
   * keeps the viewport pinned at 0. A CTA aimed at anything in the last
   * viewport-height — a footer anchor, the last section's anchor_id — would
   * otherwise strand the page exactly the way a #fragment landing does.
   */
  scrollTargetIntoView(target, scrollBehavior());

  moveFocus(target);
});
